import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Airdrie = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e9%98%bf%e5%b0%94%e4%bc%af%e5%a1%94%e7%9c%81-%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/alberta-neighbourhoods/'

  const negotiateRentLink = isChinese
    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e4%b8%8e%e6%88%bf%e4%b8%9c%e5%95%86%e8%ae%a8%e7%a7%9f%e9%87%91%e7%9a%846%e4%b8%aa%e6%8a%80%e5%b7%a7/'
    : 'https://liv.rent/blog/renters/how-to-negotiate-rent/'

  const getAbout = () => {
    return (
      <Trans i18nKey="seo.city.airdrie:About Airdrie Paragraph 1">
        <Paragraph>
          Airdrie, <LinkText href="/rental-listings/alberta">Alberta</LinkText>,
          is a dynamic, fast-growing city situated just north of Calgary,
          characterized by a friendly, small-town community spirit that coexists
          with its expanding urban amenities. Known for its commitment to
          family-friendly living, Airdrie boasts a plethora of parks,
          recreational facilities, and a network of pathways that encourage an
          active and accessible lifestyle for residents of all ages. The city’s
          economy is diverse, with a mix of local businesses and sectors,
          although its proximity to{' '}
          <LinkText href="/rental-listings/city/calgary">Calgary</LinkText>{' '}
          means it also serves as a comfortable commuting hub for professionals.
          Community events, such as the Airdrie Festival of Lights and the
          Airdrie Pro Rodeo, reflect the city’s blend of contemporary living
          with a nod to its Western heritage, fostering a sense of belonging and
          cultural appreciation. Moreover, Airdrie takes pride in its assortment
          of shopping districts, dining options, and educational facilities,
          making it an attractive location for individuals and families seeking
          a balanced blend of rural charm and modern convenience.
        </Paragraph>
      </Trans>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,000 - $2,000',
          bed2: '$1,700 - $2,700',
          bed3: '$2,300 - $3,600'
        }}
        rentReportCity={t('states:Alberta')}
        rentReportLink="https://liv.rent/blog/rent-reports/alberta/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.airdrie:Apartment Hunting in Airdrie Paragraph 1">
            Finding affordable rentals in Airdrie is easier compared to other
            major cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and <LinkText href="/rental-listing/city/toronto">Toronto</LinkText>
            , especially if you’re looking for{' '}
            <LinkText href={links.under1000}>rentals under $1000</LinkText>.
            Along with effective negotiation skills, you may be able to lower
            the rent price further. For helpful tips on negotiating rent, refer
            to our guide on{' '}
            <LinkText href={negotiateRentLink}>how to negotiate rent</LinkText>.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.airdrie:Apartment Hunting in Airdrie Paragraph 2">
            Airdrie offers a diverse range of neighborhoods and municipalities
            to choose from when searching for rentals. Depending on your
            preferences, you can explore various rental types, such as{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>, and
            <LinkText href={links.studio}>studio apartments</LinkText>. Whether
            you’re seeking{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> or{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText>,
            or even considering
            <LinkText href={links.longTerm}>long term</LinkText> or{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>,
            Airdrie has a variety of choices to suit your needs.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.airdrie:Apartments for rent Airdrie Paragraph">
              In Airdrie,{' '}
              <LinkText href={links.apartment}>apartments for rent</LinkText>{' '}
              are popular among single professionals, young couples, or small
              families who seek modern amenities and less maintenance. The
              average cost can range widely depending on the unit’s size,
              condition, and location. Prime areas for these types of rentals
              include Downtown, Channelside, and King’s Heights.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.airdrie:Houses for rent Airdrie Paragraph">
              <LinkText href={links.house}>Houses for rent</LinkText> are
              typically found in more family-friendly areas of Airdrie such as
              neighbourhoods like Bayside, Ravenswood, and Coopers Crossing.
              They are suitable for larger families who are seeking more space
              and privacy. Both partial houses and entire houses for rent are
              common, with a mix of older and newer homes depending on which
              part of the city you’re looking in.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent CITY Paragraph">
              Townhouses are a popular form of housing in {{ cityName }} that
              combines the best qualities of both apartments and whole houses.
              Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in {{ cityName }}
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.airdrie:Basements for rent Alberta Paragraph">
              A popular alternative for students,{' '}
              <LinkText href="https://liv.rent/blog/livrent/top-tips-for-first-time-renters/">
                first-time renters
              </LinkText>{' '}
              and anyone else seeking relatively inexpensive rental housing are
              basements for rent in {{ cityName }}. Since rent prices for
              apartments and houses can often get too expensive, many renters
              seek out basements for rent in {{ cityName }} as a cheaper
              alternative. Homeowners often rent out their basements as a way to
              offset their mortgage payments. This makes it easy for renters to
              find relatively inexpensive basements for rent in all areas of the
              city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.airdrie:Condos for rent Alberta Paragraph">
              Particularly in more densely-populated areas like Downtown{' '}
              {{ cityName }},{' '}
              <LinkText href={links.apartment}>
                condos for rent in {{ cityName }}
              </LinkText>{' '}
              are often the most common form of rental housing. These types of
              rental accommodation are perfect for those who like to be in the
              centre of the action, and don’t need too much space. If you are
              looking to rent in the city centre though, be prepared for
              significantly higher monthly rent prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.airdrie:Short term rentals Airdrie Paragraph">
              Airdrie, a rapidly growing city in Alberta, not far from Calgary,
              offers various employment opportunities, primarily because of its
              burgeoning local businesses and proximity to larger urban centers.
              While it doesn’t host extensive academic institutions itself, its
              closeness to Calgary allows residents easy access to several
              educational options.{' '}
              <LinkText href={links.shortTerm}>
                short-term rentals in Airdrie
              </LinkText>{' '}
              can be an attractive option, especially for those who are in
              transitional phases or seeking temporary accommodation, such as
              working professionals or small families. These rentals see a rise
              in demand, often during specific times of the year, correlating
              with common moving times or seasonal work. The flexibility
              provided by short-term leasing is indeed a draw, and serves as a
              key factor for renters in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are suitable
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.airdrie:Cheap rentals Airdrie Paragraph">
              Compared to other Canadian cities, rent prices in Airdrie are
              relatively cheaper. As a result, it shouldn’t be too hard to find
              something within your budget. If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Airdrie, AB
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in Airdrie where
              rent is considerably cheaper, so exploring these options is a good
              idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.airdrie:Living in Airdrie Paragraph">
          Airdrie, <LinkText href="/rental-listings/alberta">Alberta</LinkText>,
          presents a harmonious blend of small-town warmth and accessibility
          combined with urban amenities, making it particularly appealing to
          families, young professionals, and retirees alike. The city offers a
          variety of housing options, from modern apartments to spacious
          townhomes, catering to a wide range of preferences and budgets.
          Residents cherish the city’s community spirit underscored by events
          like the Airdrie Festival of Lights and Airdrie Pro Rodeo, promoting
          local culture and togetherness. Moreover, Airdrie is renowned for its
          commitment to outdoor living, with numerous parks, sports complexes,
          and a network of walking and biking trails, perfect for active
          individuals and nature enthusiasts. The city’s proximity to Calgary
          provides additional opportunities for employment and entertainment,
          yet Airdrie itself is steeped in its own unique charm and
          conveniences, offering a well-rounded, high-quality lifestyle.
        </Trans>
      </Paragraph>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.airdrie:Popular Neighbourhoods Airdrie Paragraph">
          Like any city, Airdrie is made up of smaller neighbourhoods with their
          own distinct features. Some of the most popular neighbourhoods include
          Downtown Airdrie, Coopers Crossing, King’s Heights, Ravenswood,
          Windsong, Woodside, and Prairie Springs. For more info about Airdrie,
          you can consult our blog on{' '}
          <LinkText href={neighborhoodsLink}>Alberta neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.airdrie:Local Landmarks Airdrie Paragraph">
          Airdrie, Alberta, though modest in size, boasts several landmarks that
          capture its unique local charm and community spirit. The Nose Creek
          Valley Museum is a gateway to the past, offering insights into
          Airdrie’s cultural heritage and history, while the Airdrie Festival of
          Lights, held annually in Nose Creek Park, dazzles as a significant
          seasonal attraction, drawing visitors for its stunning luminous
          displays. The Iron Horse Park embodies a slice of Canadian railway
          history, presenting an interactive miniature train experience that
          delights all ages. For nature enthusiasts, the Chinook Winds Regional
          Park stands as a testament to Airdrie’s commitment to outdoor life,
          featuring sprawling spaces for sports, picnics, and playgrounds, along
          with awe-inspiring splash parks in summer. These landmarks, among
          others, contribute to Airdrie’s identity as a city that values its
          heritage, community, and the natural environment, making it a charming
          place for both residents and visitors.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Airdrie
